import { useState, useMemo } from 'preact/hooks'
import { formatTime } from '../utils/dateUtils'
import { isSessionHappeningNow, isSessionEnded } from '../utils/timeUtils'
import { SessionCalendarButton } from './SessionCalendarButton'

export function TimelineView({ 
  sessions, 
  starredSessions, 
  categoryFilter,
  onSessionClick,
  onToggleStar,
  onExportComplete,
  currentTime = new Date()
}) {
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null)
  
  // Group sessions by time slots
  const timeSlots = useMemo(() => {
    const filteredSessions = categoryFilter === 'All' 
      ? sessions 
      : sessions.filter(session => session.category === categoryFilter)
    
    if (filteredSessions.length === 0) return []
    
    // Sort sessions by start time first
    const sortedSessions = [...filteredSessions].sort((a, b) => 
      new Date(a.startTime) - new Date(b.startTime)
    )
    
    // Group sessions by exact time ranges (identical start AND end times)
    const timeRangeMap = new Map()
    
    sortedSessions.forEach(session => {
      const timeKey = `${session.startTime}-${session.endTime}`
      
      if (!timeRangeMap.has(timeKey)) {
        timeRangeMap.set(timeKey, {
          startTime: session.startTime,
          endTime: session.endTime,
          sessions: []
        })
      }
      
      timeRangeMap.get(timeKey).sessions.push(session)
    })
    
    // Convert map to array and sort by start time
    return Array.from(timeRangeMap.values()).sort((a, b) => 
      new Date(a.startTime) - new Date(b.startTime)
    )
  }, [sessions, categoryFilter])
  
  const SessionBlock = ({ session, isInSlot = false }) => {
    const isStarred = starredSessions.includes(session.id)
    const isLive = isSessionHappeningNow(session, currentTime)
    const isEnded = isSessionEnded(session, currentTime)
    
    return (
      <div
        onClick={() => onSessionClick?.(session.id)}
        className={`
          p-3 rounded-lg border cursor-pointer transition-all duration-200
          transform hover:scale-[1.02] hover:z-10 relative
          ${isStarred 
            ? 'bg-editions-orange/10 border-editions-orange/40 ring-1 ring-editions-orange/30' 
            : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-editions-purple/30'
          }
          ${isLive ? 'ring-2 ring-editions-green/50 shadow-lg shadow-editions-green/20' : ''}
          ${isEnded ? 'opacity-50 grayscale hover:opacity-60' : ''}
          ${isInSlot ? 'mb-2' : 'mb-3'}
        `}
      >
        {/* Session Header */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <h4 className={`font-medium text-sm leading-tight mb-1 ${
              isLive ? 'live-session-animated' : 'text-white'
            }`}>
              {session.title}
            </h4>
            
            {/* Status indicators */}
            <div className="flex items-center gap-2 mb-1">
              {isLive && (
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-editions-green rounded-full animate-pulse"></div>
                  <span className="text-xs text-editions-green font-medium">LIVE</span>
                </div>
              )}
              {isEnded && !isLive && (
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                  <span className="text-xs text-gray-500 font-medium">ENDED</span>
                </div>
              )}
              {isStarred && (
                <div className="flex items-center gap-1">
                  <svg className="w-3 h-3 text-editions-orange fill-current" viewBox="0 0 24 24">
                    <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                  <span className="text-xs text-editions-orange">Starred</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-1 flex-shrink-0">
            <SessionCalendarButton
              session={session}
              isExpanded={false}
              onExportComplete={onExportComplete}
            />
            <button
              onClick={(e) => {
                e.stopPropagation()
                onToggleStar?.(session.id)
              }}
              className={`
                p-1 rounded transition-colors
                ${isStarred 
                  ? 'text-editions-orange hover:bg-editions-orange/20' 
                  : 'text-gray-400 hover:text-editions-orange hover:bg-editions-orange/10'
                }
              `}
            >
              <svg className="w-4 h-4" fill={isStarred ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Session details */}
        <div className="text-xs text-gray-400 space-y-1">
          <div className="flex items-center gap-2">
            <span>📍 {session.location}</span>
          </div>
          <div className="flex items-center gap-2">
            <span>🕒 {formatTime(session.startTime)} - {formatTime(session.endTime)}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full font-medium ${
              session.category === 'Dev mode' 
                ? 'bg-editions-green/20 text-editions-green'
                : session.category === 'Biz mode'
                ? 'bg-editions-purple/20 text-editions-purple-light'
                : 'bg-gray-600/20 text-gray-300'
            }`}>
              {session.category}
            </span>
            {session.contentType && (
              <span className="px-2 py-1 rounded-full bg-white/10 text-gray-300 font-medium">
                {session.contentType}
              </span>
            )}
          </div>
          {session.speakers && session.speakers.length > 0 && (
            <div className="truncate">
              👤 {session.speakers.slice(0, 2).map(s => s.name).join(', ')}
              {session.speakers.length > 2 && ` +${session.speakers.length - 2} more`}
            </div>
          )}
        </div>
      </div>
    )
  }
  
  const TimeSlot = ({ slot, index }) => {
    const isSelected = selectedTimeSlot === index
    const isCurrentTimeSlot = slot.sessions.some(s => isSessionHappeningNow(s, currentTime))
    const hasStarredSessions = slot.sessions.some(s => starredSessions.includes(s.id))
    const isSlotEnded = slot.sessions.every(s => isSessionEnded(s, currentTime))
    
    return (
      <div className={`
        border rounded-xl transition-all duration-300
        ${isCurrentTimeSlot 
          ? 'border-editions-green/50 bg-editions-green/5' 
          : isSlotEnded
          ? 'border-white/5 bg-white/2 opacity-60'
          : 'border-white/10 bg-white/5'
        }
        ${hasStarredSessions ? 'ring-1 ring-editions-orange/30' : ''}
      `}>
        {/* Time slot header */}
        <button
          onClick={() => setSelectedTimeSlot(isSelected ? null : index)}
          className={`
            w-full p-4 text-left transition-all duration-200
            hover:bg-white/5 rounded-t-xl
            ${isSelected ? 'bg-white/10' : ''}
            ${isSlotEnded ? 'opacity-60' : ''}
          `}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className={`font-semibold mb-1 ${
                isCurrentTimeSlot ? 'text-editions-green live-session-animated' : 
                isSlotEnded ? 'text-gray-500' : 'text-white'
              }`}>
                {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                {isSlotEnded && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    (ended)
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-400">
                {slot.sessions.length === 1 ? (
                  '1 session'
                ) : (
                  <span className="text-editions-purple-light font-medium">
                    {slot.sessions.length} sessions running in parallel
                  </span>
                )}
                {hasStarredSessions && (
                  <span className="ml-2 text-editions-orange">
                    • {slot.sessions.filter(s => starredSessions.includes(s.id)).length} starred
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {isCurrentTimeSlot && (
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-editions-green rounded-full animate-pulse"></div>
                  <span className="text-xs text-editions-green font-medium">LIVE</span>
                </div>
              )}
              {isSlotEnded && !isCurrentTimeSlot && (
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                  <span className="text-xs text-gray-500 font-medium">ENDED</span>
                </div>
              )}
              <svg 
                className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                  isSelected ? 'rotate-180' : ''
                }`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </button>
        
        {/* Parallel sessions */}
        {isSelected && (
          <div className="p-4 pt-0 border-t border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {slot.sessions.map(session => (
                <SessionBlock 
                  key={session.id} 
                  session={session} 
                  isInSlot={true}
                />
              ))}
            </div>
            
            {/* Conflict warning */}
            {slot.sessions.filter(s => starredSessions.includes(s.id)).length > 1 && (
              <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-red-300 text-sm">
                  <span>⚠️</span>
                  <span className="font-medium">Schedule Conflict</span>
                  <span className="text-xs text-red-400">
                    You have multiple starred sessions in this time slot
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">Timeline View</h2>
        <p className="text-gray-400 text-sm">
          See parallel sessions and find the perfect schedule • Click time slots to expand
        </p>
        {categoryFilter !== 'All' && (
          <div className="mt-2">
            <span className="text-xs bg-editions-purple/20 text-editions-purple-light px-3 py-1 rounded-full">
              Filtered: {categoryFilter}
            </span>
          </div>
        )}
      </div>
      
      {/* Timeline */}
      {timeSlots.length > 0 ? (
        <div className="space-y-3 mt-8">
          {timeSlots.map((slot, index) => (
            <TimeSlot key={slot.startTime} slot={slot} index={index} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-400">
          <div className="text-4xl mb-4">📅</div>
          <h3 className="text-lg font-medium text-white mb-2">No sessions found</h3>
          <p className="text-sm">
            {categoryFilter !== 'All' 
              ? `No sessions in ${categoryFilter} category` 
              : 'No sessions available'
            }
          </p>
        </div>
      )}
      
      {/* Legend */}
      <div className="mt-8 p-4 bg-white/5 rounded-xl border border-white/10">
        <h4 className="font-medium text-white mb-3">Legend</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-editions-green rounded-full animate-pulse"></div>
            <span className="text-gray-300">Live Now</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-3 h-3 text-editions-orange fill-current" viewBox="0 0 24 24">
              <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
            <span className="text-gray-300">Starred Session</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 border-2 border-red-500/50 rounded"></div>
            <span className="text-gray-300">Schedule Conflict</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gray-500 rounded opacity-60"></div>
            <span className="text-gray-300">Ended Session</span>
          </div>
        </div>
      </div>
    </div>
  )
} 